import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import {
	getCachedTimestamp,
	removeCachedTuple,
	setCachedTuple,
} from './commonContentIdTimestampTupleCacheUtils.ts';

const LAST_TIME_ACCESS_REQUESTS_DIALOG_WAS_OPENED =
	keys.LAST_TIME_ACCESS_REQUESTS_DIALOG_WAS_OPENED;

type OnNewAccessRequestCallback = (shouldShowIndicator: boolean) => void;

export class NewAccessRequestIndicatorState {
	private _indicatorShown: boolean = false;
	private _subscribers: Set<OnNewAccessRequestCallback> = new Set();

	private _toggleIndicator(newState: boolean) {
		this._indicatorShown = newState;
		this._subscribers.forEach((sub) => sub(newState));
	}

	private _clearCache(contentId: string) {
		removeCachedTuple(contentId, LAST_TIME_ACCESS_REQUESTS_DIALOG_WAS_OPENED);
	}

	public subscribe(callback: OnNewAccessRequestCallback) {
		this._subscribers.add(callback);
		callback(this._indicatorShown);

		return (): void => {
			this._subscribers.delete(callback);
		};
	}

	public updateIndicatorVisibility(
		contentId: string,
		mostRecentPendingRequestCreatedAt: number,
		lookback: 'partial' | 'full',
		remainingRequests: number,
	): void {
		if (lookback === 'full') {
			// No pending request remain whatsoever.
			// No need for 🔵. No need for cache.
			if (remainingRequests === 0) {
				this._clearCache(contentId);
				this._toggleIndicator(false);
				return;
			}

			// At least one pending request left
			// Check if it was created after last time Requests dialog was seen
			// If after -> show 🔵, If before -> hide 🔵
			const shouldShowIndicator =
				mostRecentPendingRequestCreatedAt >
				getCachedTimestamp(contentId, LAST_TIME_ACCESS_REQUESTS_DIALOG_WAS_OPENED);

			this._toggleIndicator(shouldShowIndicator);
		} else {
			// At this point we know we're ONLY seeing requests X minutes back (likely 5)
			// I.e. "partial" lookback.
			const shouldShowIndicator =
				mostRecentPendingRequestCreatedAt >
				getCachedTimestamp(contentId, LAST_TIME_ACCESS_REQUESTS_DIALOG_WAS_OPENED);

			// Since this is a partial lookback we can ONLY reliably show the 🔵
			// We cannot reliably hide the 🔵, because there might be older requests
			// that still happened AFTER last time Access Requests dialog was seen
			if (shouldShowIndicator) {
				this._toggleIndicator(true);
			}
		}
	}

	public markAllRequestsAsSeen(contentId: string): void {
		this._toggleIndicator(false);
		setCachedTuple(contentId, Date.now(), LAST_TIME_ACCESS_REQUESTS_DIALOG_WAS_OPENED);
	}

	public hideIndicator(contentId: string): void {
		this._clearCache(contentId);
		this._toggleIndicator(false);
	}

	public resetSubscribers(): void {
		this._indicatorShown = false;
		this._subscribers.clear();
	}
}

export const NEW_ACCESS_REQUEST_INDICATOR_STATE_INSTANCE: NewAccessRequestIndicatorState =
	new NewAccessRequestIndicatorState();
